import type { Booking } from "@ai-admin/shared";
import { skipToken, useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { trpc, trpcErrorCode } from "../../app/trpc.js";
import { Button, Chip, Input, Label, Notice } from "../../ui/index.js";
import { addDays, atLocal, dayKey, dayStart, fmtTime, hhmmOf, useCabinet } from "../cabinet/index.js";
import { invalidateBookings } from "./invalidate.js";

type PublicBooking = Omit<Booking, "manageToken">;

/** Перенос из кабинета: свободное время мастера на выбранный день или своё время (кабинет может поставить вне графика). */
export function RescheduleForm({ booking, onDone, onCancel }: { booking: PublicBooking; onDone: () => void; onCancel: () => void }) {
  const { t, i18n } = useTranslation();
  const cab = useCabinet();
  const tz = cab.timezone;
  const [day, setDay] = useState(dayKey(booking.period.start, tz));
  const [time, setTime] = useState(hhmmOf(booking.period.start, tz));
  const serviceIds = booking.items.map((i) => i.serviceId).filter(Boolean);
  const slots = useQuery(
    trpc.calendar.slots.find.queryOptions(
      serviceIds.length > 0
        ? { salonId: booking.salonId, serviceIds, masterId: booking.masterId, from: dayStart(day, tz), to: dayStart(addDays(day, 1), tz) }
        : skipToken,
    ),
  );
  const move = useMutation(
    trpc.calendar.bookings.reschedule.mutationOptions({
      onSuccess: () => {
        invalidateBookings();
        onDone();
      },
    }),
  );
  const taken = trpcErrorCode(move.error) === "slot_taken";
  return (
    <div className="space-y-3">
      <div>
        <Label htmlFor="rs-day">{t("cal.date")}</Label>
        <Input id="rs-day" type="date" value={day} onChange={(e) => e.target.value && setDay(e.target.value)} />
      </div>
      {slots.data && slots.data.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {slots.data.slice(0, 24).map((s) => {
            const v = hhmmOf(s.start, tz);
            return (
              <Chip key={s.start.toISOString()} active={v === time} onClick={() => setTime(v)}>
                {fmtTime(s.start, tz, i18n.language)}
              </Chip>
            );
          })}
        </div>
      )}
      {slots.data?.length === 0 && <p className="text-sm text-slate-500">{t("cal.noFreeSlots")}</p>}
      <div>
        <Label htmlFor="rs-time">{t("cal.time")}</Label>
        <Input id="rs-time" type="time" required step={300} value={time} onChange={(e) => setTime(e.target.value)} />
      </div>
      {move.isError && <Notice kind="error">{taken ? t("cal.slotTaken") : t("common.error")}</Notice>}
      <div className="flex gap-2">
        <Button variant="secondary" onClick={onCancel}>
          {t("common.cancel")}
        </Button>
        <Button className="flex-1" disabled={!time || move.isPending} onClick={() => move.mutate({ bookingId: booking.id, newStart: atLocal(day, time, tz) })}>
          {t("cal.moveHere")}
        </Button>
      </div>
    </div>
  );
}
